import { useEffect, useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { Building2, User, Landmark, Bell, Wallet } from "lucide-react";

export default function SettingsPage() {
  const [form, setForm] = useState(null);

  useEffect(() => { api.get("/profile").then((r) => setForm(r.data)); }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    await api.put("/profile", {
      business_name: form.business_name || "",
      trading_name: form.trading_name || "",
      entity_type: form.entity_type || "sole_trader",
      abn: form.abn || "",
      acn: form.acn || "",
      tfn: form.tfn || "",
      gst_registered: !!form.gst_registered,
      reporting_period: form.reporting_period || "quarterly",
      accounting_method: form.accounting_method || "cash",
      reminder_enabled: !!form.reminder_enabled,
      reminder_days: parseInt(form.reminder_days) || 7,
      reminder_email: form.reminder_email || "",
      cash_balance: parseFloat(form.cash_balance) || 0,
    });
    toast.success("Business details saved");
  };

  if (!form) return <div className="text-muted-foreground">Loading…</div>;

  const isCompany = form.entity_type === "company";

  return (
    <div className="max-w-2xl">
      <div className="mb-6">
        <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight">Business Settings</h1>
        <p className="text-muted-foreground text-sm mt-1">These details drive your GST reporting and BAS.</p>
      </div>

      {/* Entity type selector */}
      <div className="grid grid-cols-2 gap-3 mb-6">
        {[
          { key: "sole_trader", label: "Sole Trader", desc: "Individual ABN & TFN", icon: User },
          { key: "company", label: "Company (Pty Ltd)", desc: "ACN + ABN, company TFN", icon: Building2 },
        ].map((opt) => (
          <button key={opt.key} onClick={() => set("entity_type", opt.key)} data-testid={`entity-${opt.key}`}
            aria-pressed={form.entity_type === opt.key}
            className={`text-left rounded-2xl border p-4 transition-all ${form.entity_type === opt.key
              ? "border-primary bg-primary/5 ring-1 ring-primary" : "border-border bg-card hover:border-primary/40"}`}>
            <div className={`w-9 h-9 rounded-lg grid place-items-center mb-2 ${form.entity_type === opt.key ? "bg-primary text-primary-foreground" : "bg-secondary text-muted-foreground"}`}>
              <opt.icon className="w-4 h-4" />
            </div>
            <div className="font-heading font-semibold text-sm">{opt.label}</div>
            <div className="text-xs text-muted-foreground">{opt.desc}</div>
          </button>
        ))}
      </div>

      <div className="rounded-2xl border border-border bg-card p-6 space-y-5">
        <div className="flex items-center gap-3 pb-2">
          <div className="w-11 h-11 rounded-xl bg-primary/10 text-primary grid place-items-center"><Landmark className="w-5 h-5" /></div>
          <div>
            <div className="font-heading font-semibold">{isCompany ? "Company registration" : "Sole trader details"}</div>
            <div className="text-xs text-muted-foreground">Used across reports and BAS</div>
          </div>
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <Label>{isCompany ? "Company name" : "Your name"}</Label>
            <Input value={form.business_name || ""} onChange={(e) => set("business_name", e.target.value)} className="mt-1.5" data-testid="settings-business-name" />
          </div>
          <div>
            <Label>Trading name <span className="text-muted-foreground">(optional)</span></Label>
            <Input value={form.trading_name || ""} onChange={(e) => set("trading_name", e.target.value)} className="mt-1.5" data-testid="settings-trading-name" placeholder="Trading as…" />
          </div>
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <Label>ABN</Label>
            <Input value={form.abn || ""} onChange={(e) => set("abn", e.target.value)} className="mt-1.5 num" data-testid="settings-abn" placeholder="00 000 000 000" />
          </div>
          {isCompany ? (
            <div>
              <Label>ACN <span className="text-muted-foreground">(company)</span></Label>
              <Input value={form.acn || ""} onChange={(e) => set("acn", e.target.value)} className="mt-1.5 num" data-testid="settings-acn" placeholder="000 000 000" />
            </div>
          ) : (
            <div>
              <Label>TFN <span className="text-muted-foreground">(individual)</span></Label>
              <Input value={form.tfn || ""} onChange={(e) => set("tfn", e.target.value)} className="mt-1.5 num" data-testid="settings-tfn" placeholder="000 000 000" />
            </div>
          )}
        </div>

        {isCompany && (
          <div>
            <Label>Company TFN</Label>
            <Input value={form.tfn || ""} onChange={(e) => set("tfn", e.target.value)} className="mt-1.5 num" data-testid="settings-company-tfn" placeholder="000 000 000" />
          </div>
        )}

        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <Label>BAS reporting period</Label>
            <Select value={form.reporting_period} onValueChange={(v) => set("reporting_period", v)}>
              <SelectTrigger className="mt-1.5" data-testid="settings-period"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="monthly">Monthly</SelectItem>
                <SelectItem value="quarterly">Quarterly</SelectItem>
                <SelectItem value="annually">Annually</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Accounting method</Label>
            <Select value={form.accounting_method} onValueChange={(v) => set("accounting_method", v)}>
              <SelectTrigger className="mt-1.5" data-testid="settings-method"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="cash">Cash</SelectItem>
                <SelectItem value="accrual">Accrual</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="flex items-center justify-between rounded-lg border border-border px-4 py-3">
          <div>
            <div className="text-sm font-medium">Registered for GST</div>
            <div className="text-xs text-muted-foreground">Turnover ≥ $75,000 requires registration</div>
          </div>
          <Switch checked={form.gst_registered} onCheckedChange={(v) => set("gst_registered", v)} data-testid="settings-gst-registered" />
        </div>

        <Button onClick={save} data-testid="save-settings-btn" className="w-full sm:w-auto">Save changes</Button>
      </div>

      <div className="rounded-2xl border border-border bg-card p-6 space-y-5 mt-6">
        <div className="flex items-center gap-3 pb-2">
          <div className="w-11 h-11 rounded-xl bg-primary/10 text-primary grid place-items-center"><Bell className="w-5 h-5" /></div>
          <div>
            <div className="font-heading font-semibold">Due-date reminders</div>
            <div className="text-xs text-muted-foreground">Get emailed before each fixed cost is due</div>
          </div>
        </div>
        <div className="flex items-center justify-between rounded-lg border border-border px-4 py-3">
          <div>
            <div className="text-sm font-medium">Email reminders</div>
            <div className="text-xs text-muted-foreground">ASIC, insurance, accountant fees & more</div>
          </div>
          <Switch checked={form.reminder_enabled} onCheckedChange={(v) => set("reminder_enabled", v)} data-testid="settings-reminder-enabled" />
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <Label>Remind me this many days before</Label>
            <Input type="number" min="1" max="60" value={form.reminder_days ?? 7} onChange={(e) => set("reminder_days", e.target.value)} className="mt-1.5" data-testid="settings-reminder-days" />
          </div>
          <div>
            <Label>Send to <span className="text-muted-foreground">(optional)</span></Label>
            <Input value={form.reminder_email || ""} onChange={(e) => set("reminder_email", e.target.value)} className="mt-1.5" data-testid="settings-reminder-email" placeholder="Defaults to your login email" />
          </div>
        </div>
        <Button onClick={save} data-testid="save-reminders-btn" className="w-full sm:w-auto">Save reminder settings</Button>
      </div>

      <div className="rounded-2xl border border-border bg-card p-6 space-y-5 mt-6">
        <div className="flex items-center gap-3 pb-2">
          <div className="w-11 h-11 rounded-xl bg-primary/10 text-primary grid place-items-center"><Wallet className="w-5 h-5" /></div>
          <div>
            <div className="font-heading font-semibold">Cash on hand</div>
            <div className="text-xs text-muted-foreground">Drives your cash-runway estimate on the dashboard</div>
          </div>
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <Label>Current cash balance (AUD)</Label>
            <Input type="number" step="0.01" value={form.cash_balance ?? 0} onChange={(e) => set("cash_balance", e.target.value)} className="mt-1.5 num" data-testid="settings-cash-balance" placeholder="0.00" />
          </div>
        </div>
        <Button onClick={save} data-testid="save-cash-btn" className="w-full sm:w-auto">Save cash balance</Button>
      </div>
    </div>
  );
}
